import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, output, viewChild, inject, signal } from '@angular/core';
import { BsModalRef, ModalDirective } from 'ngx-bootstrap/modal';
import { HttpService } from 'src/app/services/http.service';
import { Pays } from '../../bo/pays';
import { ProfilConfigurationSaisieDonneesAnalyse } from '../../bo/profilConfigurationSaisieDonneesAnalyse';
import { Region, RegionStats } from '../../bo/region';
import { Site } from '../../bo/site';
import { FonctionsService } from '../../services/fonctions-partagees.service';
import { CoordinateTransform } from 'src/app/bo/coordinateTransform';
import { Coordinate } from 'src/app/bo/coordinate';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { OperationType } from 'src/app/bo/operationTypeEnum';

@Component({
  selector: 'app-site-modale',
  templateUrl: './site-modale.component.html',
  styleUrls: ['./site-modale.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteModaleComponent {
  readonly modalConfirmCancel = viewChild<ModalDirective>('modalConfirmCancel');
  private readonly translate = inject(TranslateService);
  
  site: Site;
  siteSauv: Site;
  estMiseJour: boolean;
  paysSelectionne = signal<Pays>(null);
  regionSelectionnee = signal<Region>(null);
  tabPays: Pays[];
  tabRegions: Region[];
  success = false;
  msgResult: string;
  nomInvalide = signal(false);
  paysInvalide = signal(false);
  regionInvalide = signal(false);
  estModif: boolean;
  tabProfils: ProfilConfigurationSaisieDonneesAnalyse[];
  profilSelectionne: ProfilConfigurationSaisieDonneesAnalyse = null;
  tabOperationTypes: any[] = [];
  operationTypeSelectionne: any;
  regionBackup: Region;
  paysBackup: Pays;
  erreurApi = signal(false);
  erreurCoord = signal(false);
  epsgSelectionneSite: string;
  epsgSelectionneCoordinates: string;
  epsgModif = false;
  x: any;
  y: any;
  submitEvent = output<void>();

  newTransformCoordinate: Coordinate;
  magVar = signal<number>(0);
  meridianConvergence = signal<number>(0);
  private initialMagVar = 0;
  private initialMeridianConvergence = 0;
  siteFieldTouched = false;

  constructor(public bsModalRef: BsModalRef, private readonly http: HttpService, public fonctionsService: FonctionsService) { }

  @HostListener('window:keydown.esc', ['$event'])
  onEsc(event: any): void {
    if (event.keyCode === 27) {
      event.preventDefault();
    }
    this.showModalCancel();
  }

  ngOnInit() {
    this.siteSauv = new Site();
    if (!this.estModif) {
      this.site = new Site();
      this.site.isActif = true;
    } else {
      Object.assign(this.siteSauv, this.site);
    }
    this.epsgSelectionneSite = this.site.epsg;
    this.x = this.siteSauv.coordX;
    this.y = this.siteSauv.coordY;
    this.chargerOperationTypes();
    if (this.estModif && this.site.operationType) {
      this.operationTypeSelectionne = this.site.operationType;
    } else if (!this.estModif) {
      this.operationTypeSelectionne = OperationType.OP;
    }

    if (this.estModif && this.site.siteId) {
      this.http.get<any>('sites/' + this.site.siteId + '/MagVar').subscribe(result => {
        if (result) {
          this.magVar.set(result.magVar ?? 0);
          this.meridianConvergence.set(result.meridianConvergence ?? 0);
          this.initialMagVar = result.magVar ?? 0;
          this.initialMeridianConvergence = result.meridianConvergence ?? 0;
        }
      });
    }

  }

  onSubmit() {
    this.erreurApi.set(false);
    if (this.validationFormulaire()) {
      this.fonctionsService.loading_screen(true);

      const siteChanged = !this.estModif || this.siteFieldTouched;
      const magVarChanged = this.magVar() !== this.initialMagVar
        || this.meridianConvergence() !== this.initialMeridianConvergence;

      this.site.nom = this.site.nom.trim();
      this.site.paysId = this.paysSelectionne().paysId;
      this.site.regionId = this.regionSelectionnee().regionId;
      this.site.pays = this.paysSelectionne();
      this.site.region = this.regionSelectionnee();
      this.site.profilId = this.profilSelectionne ? this.profilSelectionne.profilId : null;
      this.site.epsg = this.epsgModif ? this.epsgSelectionneSite : this.siteSauv.epsg;
      this.site.epsgCoordinates = this.epsgSelectionneCoordinates;
      this.site.coordX = this.x;
      this.site.coordY = this.y;
      this.site.operationType = this.operationTypeSelectionne;

      const promises: Promise<any>[] = [];

      if (siteChanged) {
        const requeteSite = this.estModif ? this.http.put<Site, Site>('Sites', this.site) :
          this.http.post<Site, Site>('Sites', this.site);
        promises.push(requeteSite.toPromise().then(site => {
          if (site != null) {
            this.site = site as Site;
          }
        }));
      }

      if (magVarChanged && this.site.siteId) {
        promises.push(this.http.put('sites/' + this.site.siteId + '/MagVar', {
          magVar: this.magVar(),
          meridianConvergence: this.meridianConvergence()
        }).toPromise());
      }

      if (promises.length === 0) {
        this.fonctionsService.loading_screen(false);
        this.bsModalRef.hide();
        return;
      }

      Promise.all(promises)
        .then(() => {
          this.success = true;
          this.submitEvent.emit();
          this.fonctionsService.loading_screen(false);
          this.fonctionsService.launch_toast(true);
          this.bsModalRef.hide();
        })
        .catch(err => {
          this.success = false;
          this.fonctionsService.loading_screen(false);
          if (err?.status == 400) {
            this.erreurCoord.set(true);
          } else {
            this.erreurApi.set(true);
          }
        });
    }
  }

  validationFormulaire() {
    this.nomInvalide.set(this.fonctionsService.stringEstVide(this.site.nom));
    this.paysInvalide.set(!this.paysSelectionne());
    this.regionInvalide.set(!this.regionSelectionnee());
    this.erreurCoord.set(this.x == null || this.x == undefined || this.y == null || this.y == undefined);
    if (!this.erreurCoord()) {
      this.x = this.x.toString().replace(/,/, '.');
      this.y = this.y.toString().replace(/,/, '.');
    }

    return !this.nomInvalide() && !this.paysInvalide() && !this.regionInvalide() && !this.erreurCoord();
  }

  showModalCancel() {
    if (this.diff()) {
      this.modalConfirmCancel()?.show();
    } else {
      this.annulModif();
    }
  }

  diff() {
    try{
      if (this.estModif) {
        return this.site.nom !== this.siteSauv.nom ||
          this.site.isActif !== this.siteSauv.isActif ||
          this.regionSelectionnee().regionId !== this.regionBackup.regionId ||
          this.paysSelectionne().paysId !== this.paysBackup.paysId ||
          this.operationTypeSelectionne !== this.siteSauv.operationType ||
          this.profilSelectionne ||
          this.epsgSelectionneSite !== this.siteSauv.epsg ||
          this.x !== this.siteSauv.coordX ||
          this.y !== this.siteSauv.coordY;
      } else {
        return this.site.nom ||
          this.regionSelectionnee() ||
          this.paysSelectionne() ||
          this.operationTypeSelectionne ||
          this.profilSelectionne ||
          this.epsgSelectionneSite ||
          this.x ||
          this.y;
      }
    }
    catch(e){
      console.error(e);
      return false;
    }
    
  }

  annulModif() {
    this.site.siteId = -1;
    this.bsModalRef.hide();
  }

  getEPSGSite(epsg: string) {
    this.epsgSelectionneSite = epsg;
  }


  getEPSGCoordinates(epsg: CoordinateTransform) {
    this.epsgSelectionneCoordinates = epsg.EPSGout.toString();
    console.log(this.epsgSelectionneCoordinates)
    if(this.estModif && this.site.epsg != undefined && this.x != undefined && this.y != undefined){
        this.fonctionsService.loading_screen(true);
        const ct =  new CoordinateTransform();
        ct.EPSGin = epsg.EPSGin;
        ct.EPSGout = epsg.EPSGout;
        ct.coordX = this.x;
        ct.coordY = this.y;

        this.http.put<CoordinateTransform, Coordinate>('Sites/TransformCoordinate', ct).subscribe({
          next: result =>{
              this.newTransformCoordinate = result;
              this.fonctionsService.loading_screen(false);
          },
          error: (error: HttpErrorResponse) => {
            if(error.status === 401 || error.status === 403){
          console.log(error.error);
            }
            this.fonctionsService.launch_toast(false);
            this.fonctionsService.loading_screen(false);
          }
        })
    }
  }

  isModifEPSG(isModif: boolean) {
    this.epsgModif = isModif;
  }

  getCoordX(coord) {
    this.x = coord;
  }

  getCoordY(coord) {
    this.y = coord;
  }
  chargerOperationTypes() {
    this.tabOperationTypes = Object.keys(OperationType)
      .filter(key => isNaN(Number(key)))
      .map(key => ({
        id: OperationType[key as keyof typeof OperationType],
        nom: this.translate.instant(`OPERATION_TYPE.${key}`)
      }));
  }
}
